/**
 * JIRA Epic Mapper
 * Handles epic overflow management and OpenAI-based epic mapping
 */

import { EPICS_DATA } from './EpicsData';

/**
 * Get available Peepz epics for mapping
 * Combines existing project epics with default epics from EPICS_DATA
 */
export async function getAvailableEpics(supabase, user, projectType) {
  const availableEpics = [];
  
  // Get existing project epics
  if (supabase && user?.proj?.id) {
    const { data: existingEpics } = await supabase
      .from('work')
      .select('id, title, description')
      .eq('project_id', user.proj.id)
      .eq('type', 'epic')
      .is('parent_id', null);
    
    if (existingEpics) {
      existingEpics.forEach(epic => {
        availableEpics.push({
          id: epic.id,
          title: epic.title,
          description: epic.description,
          source: 'existing'
        });
      });
    }
  }
  
  // Add default epics from EPICS_DATA
  if (projectType && EPICS_DATA[projectType]) {
    EPICS_DATA[projectType].epics.forEach(epic => {
      // Only add if not already in list (avoid duplicates)
      if (!availableEpics.find(e => e.title === epic.title)) {
        availableEpics.push({
          id: null, // Not created yet
          title: epic.title,
          description: epic.description,
          source: 'default'
        });
      }
    });
  }
  
  return availableEpics;
}

/**
 * Fuzzy match JIRA epic to Peepz epic using string similarity
 * Returns the best matching epic or null if no good match found
 */
function fuzzyMatchEpic(jiraEpicTitle, availableEpics) {
  if (!jiraEpicTitle || !availableEpics || availableEpics.length === 0) {
    return null;
  }
  
  const jiraLower = jiraEpicTitle.toLowerCase().trim();
  
  // 1. Exact match (case-insensitive)
  let match = availableEpics.find(e => e.title.toLowerCase() === jiraLower);
  if (match) return match;
  
  // 2. JIRA epic contains Peepz epic name (e.g., "Island Gameplay" contains "Gameplay")
  match = availableEpics.find(e => {
    const epicLower = e.title.toLowerCase();
    return jiraLower.includes(epicLower) || epicLower.includes(jiraLower);
  });
  if (match) return match;
  
  // 3. Word-based matching - check if key words match
  const jiraWords = jiraLower.split(/[\s\-_]+/).filter(w => w.length > 2);
  let bestMatch = null;
  let bestScore = 0;
  
  for (const epic of availableEpics) {
    const epicLower = epic.title.toLowerCase();
    const epicWords = epicLower.split(/[\s\-_]+/).filter(w => w.length > 2);
    
    // Count matching words
    let score = 0;
    for (const jiraWord of jiraWords) {
      if (epicWords.some(epicWord => epicWord.includes(jiraWord) || jiraWord.includes(epicWord))) {
        score++;
      }
    }
    
    // If at least one significant word matches, consider it
    if (score > 0 && score > bestScore) {
      bestScore = score;
      bestMatch = epic;
    }
  }
  
  // Only return if we have a reasonable match (at least one word matches)
  if (bestMatch && bestScore > 0) {
    return bestMatch;
  }
  
  return null;
}

/**
 * Batch map multiple JIRA epics using OpenAI
 * Returns a Map of jiraEpic summary -> matched Peepz epic
 */
async function batchSuggestEpicMappings(jiraEpics, availableEpics) {
  if (!jiraEpics || jiraEpics.length === 0 || !availableEpics || availableEpics.length === 0) {
    return new Map();
  }
  
  try {
    const response = await fetch('/.netlify/functions/gpt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        purpose: 'map_epic',
        jira_epics: jiraEpics.map(epic => ({
          summary: epic.summary,
          description: epic.description || ''
        })),
        available_epics: availableEpics.map(e => ({
          title: e.title,
          description: e.description || ''
        }))
      })
    });
    
    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }
    
    const data = await response.json();
    const mappings = new Map();
    
    // Try to use batchMappings if available (from JSON response)
    if (data.batchMappings && typeof data.batchMappings === 'object') {
      for (const [jiraSummary, peepzTitle] of Object.entries(data.batchMappings)) {
        const matchedEpic = availableEpics.find(e => 
          e.title.toLowerCase() === String(peepzTitle).toLowerCase()
        );
        if (matchedEpic) {
          mappings.set(jiraSummary, matchedEpic);
        }
      }
    } else {
      // Fallback: try to parse the message as JSON
      try {
        let jsonStr = data.message?.trim() || '';
        if (jsonStr.startsWith('```')) {
          jsonStr = jsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        }
        const parsed = JSON.parse(jsonStr);
        for (const [jiraSummary, peepzTitle] of Object.entries(parsed)) {
          const matchedEpic = availableEpics.find(e => 
            e.title.toLowerCase() === String(peepzTitle).toLowerCase()
          );
          if (matchedEpic) {
            mappings.set(jiraSummary, matchedEpic);
          }
        }
      } catch (parseError) {
        console.error('Error parsing batch response:', parseError);
      }
    }
    
    return mappings;
  } catch (error) {
    console.error('Error getting batch epic mapping suggestions:', error);
    return new Map();
  }
}

/**
 * Use OpenAI to suggest which Peepz epic a JIRA Epic should map to
 * First tries fuzzy matching, then falls back to OpenAI (single mode for backward compatibility)
 */
export async function suggestEpicMapping(jiraEpic, availableEpics) {
  if (!availableEpics || availableEpics.length === 0) {
    return null;
  }
  
  // First, try fuzzy matching (fast and free)
  const fuzzyMatch = fuzzyMatchEpic(jiraEpic.summary, availableEpics);
  if (fuzzyMatch) {
    console.log(`Fuzzy matched "${jiraEpic.summary}" to "${fuzzyMatch.title}"`);
    return fuzzyMatch;
  }
  
  // If no fuzzy match, use OpenAI (single mode for backward compatibility)
  // eslint-disable-next-line no-unused-vars
  const epicList = availableEpics.map(e => `- ${e.title}${e.description ? `: ${e.description}` : ''}`).join('\n');
  
  try {
    const response = await fetch('/.netlify/functions/gpt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        purpose: 'map_epic',
        jira_epic: {
          summary: jiraEpic.summary,
          description: jiraEpic.description || ''
        },
        available_epics: availableEpics.map(e => ({
          title: e.title,
          description: e.description || ''
        }))
      })
    });
    
    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }
    
    const data = await response.json();
    const suggestedTitle = data.message?.trim();
    
    // Find matching epic
    const matchedEpic = availableEpics.find(e => 
      e.title.toLowerCase() === suggestedTitle.toLowerCase()
    );
    
    return matchedEpic || availableEpics[0]; // Fallback to first epic
  } catch (error) {
    console.error('Error getting epic mapping suggestion:', error);
    // Fallback: return first available epic
    return availableEpics[0];
  }
}

/**
 * Determine epic mapping strategy based on count
 */
export function determineEpicStrategy(jiraEpics) {
  const epicCount = jiraEpics.length;
  
  return {
    count: epicCount,
    overflow: epicCount > 12,
    strategy: epicCount > 12 ? 'all_to_subepics' : 'epics_as_epics'
  };
}

/**
 * Map JIRA Epics to Peepz epics/subepics with OpenAI suggestions
 */
export async function mapJiraEpics(jiraEpics, supabase, user, projectType) {
  if (!jiraEpics || jiraEpics.length === 0) {
    return {
      strategy: 'epics_as_epics',
      mappings: []
    };
  }
  
  const strategy = determineEpicStrategy(jiraEpics);
  const availableEpics = await getAvailableEpics(supabase, user, projectType);
  
  if (strategy.overflow) {
    // All JIRA Epics become Subepics
    // First, try fuzzy matching for all epics
    const fuzzyMatched = new Map(); // jiraEpic summary -> matched epic
    const needsOpenAI = []; // Epics that need OpenAI mapping
    
    for (const jiraEpic of jiraEpics) {
      if (!jiraEpic || !jiraEpic.summary) {
        continue;
      }
      
      const fuzzyMatch = fuzzyMatchEpic(jiraEpic.summary, availableEpics);
      if (fuzzyMatch) {
        fuzzyMatched.set(jiraEpic.summary, fuzzyMatch);
        console.log(`Fuzzy matched "${jiraEpic.summary}" to "${fuzzyMatch.title}"`);
      } else {
        needsOpenAI.push(jiraEpic);
      }
    }
    
    // Batch OpenAI requests for epics that need it
    let batchMappings = new Map();
    if (needsOpenAI.length > 0) {
      console.log(`Batching ${needsOpenAI.length} epics for OpenAI mapping`);
      batchMappings = await batchSuggestEpicMappings(needsOpenAI, availableEpics);
    }
    
    // Combine fuzzy matches and OpenAI batch results
    const mappings = jiraEpics
      .filter(jiraEpic => jiraEpic && jiraEpic.summary)
      .map((jiraEpic) => {
        // Check fuzzy matches first
        let suggestedEpic = fuzzyMatched.get(jiraEpic.summary);
        
        // If not fuzzy matched, check batch OpenAI results
        if (!suggestedEpic) {
          suggestedEpic = batchMappings.get(jiraEpic.summary);
        }
        
        // Fallback to first available epic if still no match
        if (!suggestedEpic && availableEpics.length > 0) {
          suggestedEpic = availableEpics[0];
        }
        
        return {
          jiraEpic,
          targetEpic: suggestedEpic,
          type: 'subepic',
          peepzSubepic: null // Will be created during import
        };
      });
    
    return {
      strategy: 'all_to_subepics',
      mappings
    };
  } else {
    // JIRA Epics become Peepz Epics
    return {
      strategy: 'epics_as_epics',
      mappings: jiraEpics
        .filter(jiraEpic => jiraEpic && jiraEpic.summary)
        .map(jiraEpic => ({
          jiraEpic,
          targetEpic: null, // Will become a new epic
          type: 'epic',
          peepzSubepic: null // Will be created during import as default subepic
        }))
    };
  }
}

